// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, AlertCircle, KeyRound, Plus, ShieldCheck, Copy, Check, ChevronRight, ChevronDown, Search, Trash2, ScrollText } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import DataApiWebhooksSection, { WebhookDeliveriesPanel } from './DataApiWebhooksSection'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'

// D-API-1 Phase 2 — Pharmacy Settings → Data API. SaaS admin enables the module; the pharmacy admin
// (data_api.manage) provisions external partner clients: keys (shown once), client cert, provider
// scope, per-field access (hidden / masked / raw), plus suspend / delete / audit. Provider and field
// pickers are searchable so they scale to hundreds of entries.
const MODES = ['hidden', 'masked', 'raw'] as const
type Mode = typeof MODES[number]
const MODE_META: Record<Mode, { label: string; color: string }> = {
  hidden: { label: 'Hidden', color: '#6b7280' }, masked: { label: 'Masked', color: '#d97706' }, raw: { label: 'Raw', color: '#16a34a' },
}

export default function DataApiSettingsTab() {
  const [boot, setBoot] = useState<api.DataApiBootstrap | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [showNew, setShowNew] = useState(false)

  function load() {
    setLoading(true)
    api.getDataApiConfig().then(b => { setBoot(b); setErr(null) }).catch(e => setErr(friendlyError(e))).finally(() => setLoading(false))
  }
  // TOP-LEVEL TABS (owner, 2026-09-18). The page was partners + a pharmacy-wide deliveries table on
  // one scroll, with no API-key visibility at all. Four questions, four tabs.
  const [topTab, setTopTab] = useState<PharmTab>('partners')

  const [clientQ, setClientQ] = useState('')
  const [clientStatus, setClientStatus] = useState('')
  const [clientPage, setClientPage] = useState(0)
  const CLIENTS_PER_PAGE = 8

  useEffect(() => { load() }, [])

  const filteredClients = (boot?.clients ?? []).filter(c => {
    const q = clientQ.trim().toLowerCase()
    if (q && !c.name.toLowerCase().includes(q) && !(c.purpose ?? '').toLowerCase().includes(q)) return false
    if (clientStatus && c.status !== clientStatus) return false
    return true
  })
  const pageCount = Math.max(1, Math.ceil(filteredClients.length / CLIENTS_PER_PAGE))
  const pagedClients = filteredClients.slice(clientPage * CLIENTS_PER_PAGE, (clientPage + 1) * CLIENTS_PER_PAGE)

  return (
    // Was maxWidth: 920, which capped the page on any monitor wider than a laptop while the
    // delivery table underneath wants every pixel it can get (owner feedback, 2026-09-18). A
    // readable measure still matters for the prose, so the paragraph keeps its own cap rather than
    // the whole page wearing one.
    <div style={{ maxWidth: '100%' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 6px' }}><KeyRound size={18} /> Data API</h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--color-text-soft)', lineHeight: 1.5, maxWidth: 820 }}>
        Give a partner system read access to <strong>only the data you choose</strong>. Each client is scoped to specific
        providers and fields (hidden / masked / real), authenticates with an API key <strong>and a client certificate</strong>,
        and every call is logged. A key is shown <strong>once</strong> when you generate it — copy it then. A client can't be
        switched on until a BAA reference is recorded.
      </p>

      {err && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 10 }}><AlertCircle size={13} /> {err}</div>}
      {loading && !boot && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={14} className="spin" /> Loading…</div>}

      {/* Render the list whenever we have data — do NOT gate on `loading`, or a background refetch
          (e.g. after generating a key) unmounts the editor and destroys the shown-once key banner. */}
      {boot && (
        <>
          <div role="tablist" aria-label="Data API" style={topTabBar}>
            {PHARM_TABS.map(t => (
              <button key={t.id} role="tab" id={`dapi-tab-${t.id}`}
                aria-selected={topTab === t.id} aria-controls={`dapi-panel-${t.id}`}
                tabIndex={topTab === t.id ? 0 : -1}
                onClick={() => setTopTab(t.id)}
                onKeyDown={e => {
                  const i = PHARM_TABS.findIndex(x => x.id === topTab)
                  if (e.key === 'ArrowRight') setTopTab(PHARM_TABS[(i + 1) % PHARM_TABS.length].id)
                  if (e.key === 'ArrowLeft') setTopTab(PHARM_TABS[(i - 1 + PHARM_TABS.length) % PHARM_TABS.length].id)
                }}
                style={topTab === t.id ? topTabOn : topTabOff}>{t.label}</button>
            ))}
          </div>

          <div role="tabpanel" id={`dapi-panel-${topTab}`} aria-labelledby={`dapi-tab-${topTab}`} tabIndex={0} style={{ outline: 'none' }}>
          {topTab === 'usage' && <UsagePanel onError={setErr} />}
          {topTab === 'deliveries' && <WebhookDeliveriesPanel partners={boot.clients} onError={setErr} />}
          {topTab === 'keylog' && <KeyLogPanel clients={boot.clients} onError={setErr} />}

          {topTab === 'partners' && (<>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setShowNew(v => !v)} style={btnPrimary} disabled={boot.limits.clients_used >= boot.limits.max_clients}><Plus size={14} /> New client</button>
            <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
              {boot.limits.clients_used} of {boot.limits.max_clients} clients used
              {boot.limits.clients_used >= boot.limits.max_clients && ' — limit reached (contact your account executive to raise it)'}
            </span>
          </div>
          {showNew && boot.limits.clients_used < boot.limits.max_clients && <NewClientForm onCreated={() => { setShowNew(false); load() }} onError={setErr} />}
          {boot.clients.length === 0 && !showNew && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No partner clients yet. Create one to begin.</div>}

          {/* SEARCH + FILTER + PAGING (owner feedback, 2026-09-18: "uncontrolled if i have like 20
              client"). The list was every client, unpaged, as collapsed accordion rows — fine at
              two, a wall to scroll at twenty, with no way to find one by name or to see just the
              pending ones. Filtering is CLIENT-side deliberately: max_clients is a small per-pharmacy
              number (default 10), so the whole list is already in memory from the bootstrap call and
              a server round trip per keystroke would be slower and no more correct. The DELIVERY
              tables, which are unbounded, are server-paged — see WebhookDeliveriesPanel. */}
          {boot.clients.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <input
                value={clientQ}
                onChange={e => { setClientQ(e.target.value); setClientPage(0) }}
                placeholder="Search partners by name…"
                aria-label="Search partner clients by name"
                style={{ ...inpBase, flex: '1 1 240px', maxWidth: 360 }}
              />
              <div style={{ width: 150 }}>
                <SearchableSelect
                  value={clientStatus}
                  onChange={v => { setClientStatus(v); setClientPage(0) }}
                  ariaLabel="Filter partner clients by status"
                  placeholder="All statuses"
                  options={[
                    { value: '', label: 'All statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'suspended', label: 'Suspended' },
                  ]}
                />
              </div>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
                {filteredClients.length} of {boot.clients.length} shown
              </span>
            </div>
          )}

          {filteredClients.length === 0 && boot.clients.length > 0 && (
            <div style={{ fontSize: 13, color: 'var(--color-text-soft)', padding: '8px 0' }}>
              No partner matches that search.
            </div>
          )}

          {pagedClients.map(c => (
            <div key={c.id} style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 8, marginBottom: 10 }}>
              <button onClick={() => setExpanded(x => x === c.id ? null : c.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                {expanded === c.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                <StatusBadge status={c.status} />
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-text-soft)' }}>
                  {c.active_keys} key{c.active_keys === 1 ? '' : 's'} · {c.provider_count} provider{c.provider_count === 1 ? '' : 's'} · {c.grant_count} field{c.grant_count === 1 ? '' : 's'}{c.cert_pinned ? ' · cert ✓' : ' · no cert'}
                </span>
              </button>
              {expanded === c.id && <ClientEditor client={c} catalog={boot.catalog} providers={boot.providers} reports={boot.reports} maxKeys={boot.limits.max_keys_per_client} onChanged={load} onError={setErr} />}
            </div>
          ))}

          {pageCount > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 18px' }}>
              <button type="button" style={btn} disabled={clientPage === 0}
                onClick={() => setClientPage(p => Math.max(0, p - 1))}>Previous</button>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }} aria-live="polite">
                Page {clientPage + 1} of {pageCount}
              </span>
              <button type="button" style={btn} disabled={clientPage >= pageCount - 1}
                onClick={() => setClientPage(p => Math.min(pageCount - 1, p + 1))}>Next</button>
            </div>
          )}

          </>)}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The pharmacy's OWN API usage (owner, 2026-09-18: "in the pharmacy i dont have any api keys
 * meters i have only Webhook deliveries").
 *
 * The meter has existed since Phase 3a, but only the platform could read it — so a pharmacy could
 * see every webhook delivery and nothing at all about the pull API its partners were hitting,
 * including whether one was being throttled. The budgets are shown read-only beside the numbers so
 * "why are we being refused" is answerable here rather than by asking us.
 */
function UsagePanel({ onError }: { onError: (e: string) => void }) {
  const [d, setD] = useState<api.PharmacyUsage | null>(null)
  const [days, setDays] = useState(7)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.getPharmacyDataApiUsage(days).then(setD).catch(e => onError(friendlyError(e))).finally(() => setLoading(false))
  }, [days])  // eslint-disable-line react-hooks/exhaustive-deps

  const byClient = new Map<string, { name: string; m: Record<string, number> }>()
  for (const u of d?.usage ?? []) {
    if (!byClient.has(u.client_id)) byClient.set(u.client_id, { name: u.client_name || '(deleted partner)', m: {} })
    byClient.get(u.client_id)!.m[u.metric] = u.total
  }
  const rows = [...byClient.values()]
  const wakes = rows.reduce((a, r) => a + (r.m['tenant.wakes'] ?? 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Window</label>
        <div style={{ width: 130 }}>
          <SearchableSelect
            value={String(days)}
            onChange={v => setDays(Number(v) || 7)}
            ariaLabel="Usage window"
            options={[
              { value: '1', label: 'Last 24 h' },
              { value: '7', label: 'Last 7 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
            ]}
          />
        </div>
        {d?.budgets.max_wakes_per_day != null && (
          <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
            Daily wake budget <strong>{d.budgets.max_wakes_per_day}</strong>
            {d.budgets.max_rows_per_day != null && <> · row budget <strong>{d.budgets.max_rows_per_day.toLocaleString()}</strong></>}
            {' '}(set by OwlRx)
          </span>
        )}
      </div>

      {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
          No API activity in this window. Counters start when a partner calls the API or an envelope is delivered.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <>
          <p style={{ fontSize: 12, color: 'var(--color-text-soft)', margin: '0 0 8px' }}>
            <strong>{wakes}</strong> tenant wake{wakes === 1 ? '' : 's'} in this window. A wake keeps your database
            awake for the next 60 minutes, so it — not row count — is what partner traffic actually costs.
          </p>
          <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: 'var(--color-text-soft)', paddingTop: 6 }}>
                API and webhook usage per partner, from the platform meter.
              </caption>
              <thead><tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border, #eee)' }}>
                <th style={th}>Partner</th><th style={thR}>Wakes</th><th style={thR}>API calls</th>
                <th style={thR}>Rows</th><th style={thR}>Refused</th>
                <th style={thR}>Delivered</th><th style={thR}>Attempts</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--color-border, #f3f3f3)' }}>
                    <td style={td}><strong>{r.name}</strong></td>
                    <td style={tdR}><strong>{r.m['tenant.wakes'] ?? 0}</strong></td>
                    <td style={tdR}>{r.m['pull.requests'] ?? 0}</td>
                    <td style={tdR}>{r.m['pull.rows'] ?? 0}</td>
                    <td style={tdR}>{r.m['pull.denied'] ?? 0}</td>
                    <td style={tdR}>{r.m['hook.deliveries'] ?? 0}</td>
                    <td style={tdR}>{r.m['hook.attempts'] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * API key activity across every partner — the "api key logs" tab.
 *
 * Deliberately built from the DISCLOSURE record rather than a separate log: every served pull
 * request already writes one §164.528 row, so a second log would be a second source of truth about
 * the same events, and the two would drift.
 */
// D-UI-2: the funnels of the partner key log. Server-paged, so they are sent to the endpoint
// (`filter=`), never applied to the page on screen. Keys match the endpoint's allow-list.
const KEYLOG_FUNNEL_COLS: ServerFunnelColumn[] = [
  { key: 'requested_at', label: 'When', kind: 'date' },
  { key: 'resource', label: 'Resource', kind: 'text' },
  { key: 'row_count', label: 'Rows', kind: 'number' },
  { key: 'patient_count', label: 'Patients', kind: 'number' },
  { key: 'fields', label: 'Fields', kind: 'text' },
  { key: 'status', label: 'Status', kind: 'number' },
]
// The per-partner audit shows the resource WITH its version, so its funnel matches that text.
const CLIENT_AUDIT_FUNNEL_COLS: ServerFunnelColumn[] = KEYLOG_FUNNEL_COLS.map(c =>
  c.key === 'resource' ? { ...c, key: 'resource_version' } : c)

function KeyLogPanel({ clients, onError }: { clients: api.DataApiClientRow[]; onError: (e: string) => void }) {
  const [sel, setSel] = useState<string>(clients[0]?.id ?? '')
  const [rows, setRows] = useState<api.DataApiDisclosure[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const PAGE = 50
  const fx = useServerFunnels(KEYLOG_FUNNEL_COLS)
  // A range funnel refetches as it is typed; only the newest request may land.
  const reqSeq = useRef(0)

  const fetchPage = (p: number, clientId: string) => {
    if (!clientId) return
    const seq = ++reqSeq.current
    setLoading(true)
    api.getDataApiClientAudit(clientId, { page: p, page_size: PAGE, filter: fx.filter })
      .then(r => { if (seq !== reqSeq.current) return; setRows(r.disclosures); setTotal(r.total); setPage(r.page) })
      .catch(e => { if (seq === reqSeq.current) onError(friendlyError(e)) })
      .finally(() => { if (seq === reqSeq.current) setLoading(false) })
  }
  useEffect(() => { fetchPage(0, sel) }, [sel, fx.sig])  // eslint-disable-line react-hooks/exhaustive-deps

  const pageCount = Math.max(1, Math.ceil(total / PAGE))
  const fieldsShort = (json: string | null) => { if (!json) return ''; try { const a = JSON.parse(json) as string[]; return a.length <= 3 ? a.join(', ') : `${a.slice(0, 3).join(', ')} +${a.length - 3}` } catch { return '' } }

  if (clients.length === 0) return <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No partners yet.</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Partner</label>
        {/* Data-bound and unbounded — a pharmacy with 20 partners is the case the owner raised. */}
        <div style={{ minWidth: 220 }}>
          <SearchableSelect
            value={sel}
            onChange={setSel}
            ariaLabel="Partner"
            placeholder="Select a partner…"
            options={clients.map(c => ({ value: c.id, label: c.name }))}
          />
        </div>
        <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
          Every API call this partner's key made, with what it was shown.
        </span>
      </div>

      {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>}
      {rows && rows.length === 0 && !loading && !fx.active && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No API calls recorded for this partner.</div>}
      {/* While a funnel is set the table stays mounted even when nothing matches: the header is
          where the funnel is cleared, and unmounting it would close the funnel being typed in. */}
      {rows && (rows.length > 0 || fx.active) && (
        <>
          {fx.chips}
          <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
            <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: 'var(--color-text-soft)', paddingTop: 6 }}>
                Disclosures recorded under 45 CFR §164.528 — retained six years.
              </caption>
              <thead><tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border, #eee)' }}>
                <th style={th}>When{fx.funnel('requested_at')}</th><th style={th}>Resource{fx.funnel('resource')}</th><th style={thR}>Rows{fx.funnel('row_count')}</th>
                <th style={thR}>Patients{fx.funnel('patient_count')}</th><th style={th}>Fields{fx.funnel('fields')}</th><th style={thR}>Status{fx.funnel('status')}</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} style={{ ...td, color: 'var(--color-text-soft)' }}>{loading ? 'Loading…' : 'No API calls match these filters.'}</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--color-border, #f3f3f3)' }}>
                    <td style={td}>{formatUsDate(r.requested_at)}</td>
                    <td style={td}>{r.resource}</td>
                    <td style={tdR}>{r.row_count}</td>
                    <td style={tdR}>{r.patient_count}</td>
                    <td style={td} title={r.fields ?? ''}>{fieldsShort(r.fields)}</td>
                    <td style={tdR}>{r.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PAGE && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <button type="button" style={btn} disabled={page === 0 || loading} onClick={() => fetchPage(page - 1, sel)}>Previous</button>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }} aria-live="polite">Page {page + 1} of {pageCount} · {total} call{total === 1 ? '' : 's'}</span>
              <button type="button" style={btn} disabled={page >= pageCount - 1 || loading} onClick={() => fetchPage(page + 1, sel)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const thR: React.CSSProperties = { padding: '6px 8px', textAlign: 'right', fontSize: 11, textTransform: 'uppercase' }
const tdR: React.CSSProperties = { padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

type PharmTab = 'partners' | 'usage' | 'deliveries' | 'keylog'

const PHARM_TABS: { id: PharmTab; label: string }[] = [
  { id: 'partners', label: 'Partners' },
  { id: 'usage', label: 'Usage' },
  { id: 'deliveries', label: 'Webhook deliveries' },
  { id: 'keylog', label: 'API key logs' },
]

const topTabBar: React.CSSProperties = { display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border, #e5e7eb)', margin: '0 0 16px' }
const topTabBase: React.CSSProperties = { padding: '8px 15px', border: 'none', background: 'transparent', fontSize: 13, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 }
const topTabOn: React.CSSProperties = { ...topTabBase, fontWeight: 700, color: 'var(--color-accent, #2563eb)', borderBottomColor: 'var(--color-accent, #2563eb)' }
const topTabOff: React.CSSProperties = { ...topTabBase, color: 'var(--color-text-soft, #6b7280)' }

const inpBase: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 7, fontSize: 13,
}

/** A platform-set limit, shown not edited. The default is named explicitly so "blank" reads as
 *  "the standard allowance" rather than "no limit" — which is the opposite of the truth. */
function ReadOnlyLimit({ label, value, dflt, suffix }: { label: string; value: string; dflt: string; suffix?: string }) {
  const set = value.trim() !== ''
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
        {set ? <>{value}{suffix}</> : <span style={{ color: 'var(--color-text-soft)' }}>{dflt}{suffix} <span style={{ fontSize: 11 }}>(default)</span></span>}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = { active: ['#16a34a', 'Active'], pending: ['#d97706', 'Pending'], suspended: ['#dc2626', 'Suspended'] }
  const [color, label] = map[status] ?? ['#6b7280', status]
  return <span style={{ fontSize: 11, fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 10, padding: '1px 8px' }}>{label}</span>
}

function NewClientForm({ onCreated, onError }: { onCreated: () => void; onError: (e: string) => void }) {
  const [name, setName] = useState(''); const [purpose, setPurpose] = useState(''); const [baaRef, setBaaRef] = useState(''); const [busy, setBusy] = useState(false)
  async function submit() {
    if (!name.trim()) { onError('Name is required'); return }
    setBusy(true)
    try { await api.createDataApiClient({ name: name.trim(), purpose: purpose.trim() || undefined, baaRef: baaRef.trim() || undefined }); onCreated() }
    catch (e) { onError(friendlyError(e)) } finally { setBusy(false) }
  }
  return (
    <div style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 8, padding: 14, marginBottom: 14, background: 'var(--color-bg-soft, #fafafa)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={lbl}>Client name <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Riverside Clinic EHR" /></label>
        <label style={lbl}>BAA reference <input style={inp} value={baaRef} onChange={e => setBaaRef(e.target.value)} placeholder="BAA-2026-…" /></label>
        <label style={{ ...lbl, gridColumn: '1 / 3' }}>Purpose <input style={inp} value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="What this partner needs the data for (minimum-necessary)" /></label>
      </div>
      <div style={{ marginTop: 10 }}><button onClick={submit} disabled={busy} style={btnPrimary}>{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Create</button></div>
    </div>
  )
}

type ClientTab = 'access' | 'webhooks' | 'data' | 'audit'

/** Grouped by the job, not by the table. "Keys & access" is everything that decides whether a
 *  partner can connect at all; "Data scope" is everything that decides what they then see. */
const CLIENT_TABS: { id: ClientTab; label: string }[] = [
  { id: 'access', label: 'Keys & access' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'data', label: 'Data scope' },
  { id: 'audit', label: 'Audit' },
]

const tabBar: React.CSSProperties = {
  display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border, #e5e7eb)', margin: '8px 0 14px',
}
const tabBase: React.CSSProperties = {
  padding: '7px 14px', border: 'none', background: 'transparent', fontSize: 13, cursor: 'pointer',
  borderBottom: '2px solid transparent', marginBottom: -1,
}
const tabOn: React.CSSProperties = {
  ...tabBase, fontWeight: 700, color: 'var(--color-primary, #1d4ed8)',
  borderBottomColor: 'var(--color-primary, #1d4ed8)',
}
const tabOff: React.CSSProperties = { ...tabBase, color: 'var(--color-text-soft, #6b7280)' }

function ClientEditor({ client, catalog, providers, reports, maxKeys, onChanged, onError }: {
  client: api.DataApiClientRow; catalog: api.DataApiResourceCatalog[]; providers: api.DataApiProvider[]; reports: api.DataApiReportRef[]; maxKeys: number; onChanged: () => void; onError: (e: string) => void
}) {
  const [detail, setDetail] = useState<api.DataApiClientDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const [newKey, setNewKey] = useState<string | null>(null); const [copied, setCopied] = useState(false)
  const [cert, setCert] = useState('')
  const [contact, setContact] = useState({ name: client.contact_name ?? '', email: client.contact_email ?? '', phone: client.contact_phone ?? '' })
  const [limits] = useState({
    rate: client.rate_limit_per_min?.toString() ?? '', quota: client.daily_quota?.toString() ?? '',
    page: client.max_page_size?.toString() ?? '', timeout: client.query_timeout_sec?.toString() ?? '',
  })
  const resource = catalog[0]?.resource ?? 'prescriptions'
  const [modes, setModes] = useState<Record<string, Mode>>({})
  const [scope, setScope] = useState<Set<string>>(new Set())
  const [reportScope, setReportScope] = useState<Set<string>>(new Set())

  function reloadDetail() {
    return api.getDataApiClient(client.id).then(d => {
      setDetail(d)
      const m: Record<string, Mode> = {}
      d.grants.filter(g => g.resource === resource).forEach(g => { m[g.field] = (g.mode as Mode) })
      setModes(m); setScope(new Set(d.provider_ids)); setReportScope(new Set(d.report_ids))
    })
  }
  useEffect(() => { reloadDetail().catch(e => onError(friendlyError(e))) }, [client.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fields = catalog.find(c => c.resource === resource)?.fields ?? []

  async function run(fn: () => Promise<unknown>) { setBusy(true); try { await fn(); onChanged() } catch (e) { onError(friendlyError(e)) } finally { setBusy(false) } }
  const generateKey = () => run(async () => { setNewKey(null); const r = await api.issueDataApiKey(client.id); setNewKey(r.key); await reloadDetail() })
  const revokeKey = (keyId: string) => run(async () => { await api.revokeDataApiKey(client.id, keyId); await reloadDetail() })
  const saveCert = () => run(async () => { await api.updateDataApiClient(client.id, { certFingerprint: cert.trim() }); setCert('') })
  const saveGrants = () => run(() => api.setDataApiGrants(client.id, resource, Object.entries(modes).filter(([, m]) => m !== 'hidden').map(([field, mode]) => ({ field, mode }))))
  const saveScope = () => run(() => api.setDataApiProviders(client.id, [...scope]))
  const saveReports = () => run(() => api.setDataApiReports(client.id, [...reportScope]))
  const setStatus = (status: string) => run(() => api.updateDataApiClient(client.id, { status }))
  const num = (s: string) => { const n = parseInt(s, 10); return Number.isFinite(n) && n > 0 ? n : undefined }
  const saveContactLimits = () => run(() => api.updateDataApiClient(client.id, {
    contactName: contact.name.trim(), contactEmail: contact.email.trim(), contactPhone: contact.phone.trim(),
    rateLimitPerMin: num(limits.rate), dailyQuota: num(limits.quota), maxPageSize: num(limits.page), queryTimeoutSec: num(limits.timeout),
  }))
  const del = () => { if (confirm(`Delete "${client.name}"? Its keys are revoked immediately. The disclosure/audit history is preserved.`)) run(() => api.deleteDataApiClient(client.id)) }



  const grantedCount = Object.values(modes).filter(m => m !== 'hidden').length

  const [tab, setTab] = useState<ClientTab>('access')

  if (!detail) return <div style={{ padding: 14, fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>

  return (
    <div style={{ padding: '4px 16px 16px', borderTop: '1px solid var(--color-border, #eee)' }}>
      {/* TABS, not one long scroll (owner, 2026-09-18: "i hate having long pages that kills ux").
          This editor was nine stacked sections and ~825 lines across two files: to reach the
          webhooks you scrolled past keys, cert, contact, limits, providers, fields and reports.
          Each group below is a job someone actually sits down to do, which is why "Keys & access"
          and "Webhooks" are separate tabs rather than neighbours on a page. */}
      <div role="tablist" aria-label="Partner settings" style={tabBar}>
        {CLIENT_TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`panel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            onKeyDown={e => {
              // Arrow keys move between tabs — the WAI-ARIA tab pattern. Without it a keyboard user
              // has to tab through every control in the open panel to reach the next tab.
              const i = CLIENT_TABS.findIndex(x => x.id === tab)
              if (e.key === 'ArrowRight') setTab(CLIENT_TABS[(i + 1) % CLIENT_TABS.length].id)
              if (e.key === 'ArrowLeft') setTab(CLIENT_TABS[(i - 1 + CLIENT_TABS.length) % CLIENT_TABS.length].id)
            }}
            style={tab === t.id ? tabOn : tabOff}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} tabIndex={0} style={{ outline: 'none' }}>
        {tab === 'access' && (<>
        <Section title="Status">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {client.status !== 'active' && <button style={btn} disabled={busy} onClick={() => setStatus('active')}>Activate</button>}
            {client.status === 'active' && <button style={btn} disabled={busy} onClick={() => setStatus('suspended')}>Suspend</button>}
            <button style={{ ...btn, color: '#dc2626', borderColor: '#f3c9c9' }} disabled={busy} onClick={del}><Trash2 size={13} /> Delete</button>
            <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Activation requires a BAA reference. Deleting keeps the audit history.</span>
          </div>
        </Section>
        <Section title="API keys">
          {newKey && (
            <div style={{ border: '1px solid #16a34a', background: '#f0fdf4', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Copy this key now — it won't be shown again.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input readOnly value={newKey} onFocus={e => e.currentTarget.select()} spellCheck={false}
                  aria-label="New API key — copy it now, it is shown only once"
                  style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, padding: '6px 8px', border: '1px solid #86efac', borderRadius: 4, background: '#fff' }} />
                <button style={btn} onClick={() => { navigator.clipboard?.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 1500) }}>{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}</button>
                <button style={btn} onClick={() => setNewKey(null)}>Dismiss</button>
              </div>
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 6 }}>Only a prefix is stored — the full key is shown once, at creation. If a key is lost, generate a new one and revoke the old.</div>
          {detail.keys.filter(k => !k.revoked).map(k => (
            <div key={k.id} style={rowStyle}>
              <code style={{ fontSize: 12 }}>{k.key_prefix}…</code>
              {!k.active && <span style={{ fontSize: 10, fontWeight: 600, color: '#d97706', border: '1px solid #d97706', borderRadius: 10, padding: '0 6px' }}>Inactive</span>}
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>created {formatUsDate(k.created_at)}{k.last_used_at ? ` · last used ${formatUsDate(k.last_used_at)}` : ' · never used'}</span>
              <button style={{ ...btn, marginLeft: 'auto' }} disabled={busy} onClick={() => run(async () => { await api.setDataApiKeyActive(client.id, k.id, !k.active); await reloadDetail() })}>{k.active ? 'Deactivate' : 'Activate'}</button>
              {/* Revoke is PERMANENT and cuts a live partner off on their next request, so it is
                  styled as destructive and confirmed — Deactivate beside it is reversible and is not. */}
              <button style={btnDanger} disabled={busy} title="Permanent — the partner's integration stops working immediately"
                onClick={() => { if (confirm(`Revoke key ${k.key_prefix}…?\n\nThis is permanent and cannot be undone. Any partner system using this key stops working on its next request.\n\nTo pause access temporarily instead, use Deactivate.`)) revokeKey(k.id) }}>Revoke</button>
            </div>
          ))}
          {(() => { const live = detail.keys.filter(k => !k.revoked).length; const atCap = live >= maxKeys; return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={btnPrimary} disabled={busy || atCap} onClick={generateKey}><KeyRound size={13} /> Generate key</button>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{live} of {maxKeys} keys{atCap ? ' — limit reached (revoke one first)' : ''}</span>
            </div>
          ) })()}
        </Section>
        <Section title="Client certificate (mTLS)">
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 6 }}>Pin the SHA-256 fingerprint of the partner's client certificate. {client.cert_pinned ? 'A certificate is currently pinned.' : 'No certificate pinned yet.'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...inp, flex: 1 }} value={cert} onChange={e => setCert(e.target.value)} placeholder="AB:CD:EF:… or hex fingerprint" aria-label="Client certificate fingerprint" />
            <button style={btn} disabled={busy || !cert.trim()} onClick={saveCert}><ShieldCheck size={13} /> Pin</button>
          </div>
        </Section>
        <Section title="Point of contact">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <input style={inp} value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} placeholder="Contact name" aria-label="Partner contact name" />
            <input style={inp} value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} placeholder="Email" aria-label="Partner contact email" />
            <input style={inp} value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} placeholder="Phone" aria-label="Partner contact phone" />
          </div>
          <div style={{ marginTop: 10 }}><button style={btnPrimary} disabled={busy} onClick={saveContactLimits}>Save contact</button></div>
        </Section>
        {/* READ-ONLY (owner, 2026-09-18: "its not for the customers at all"). Rate, quota, page size
            and query timeout decide what the platform is willing to serve and what it costs us to
            serve it — a pharmacy raising its own rate limit is a customer editing its own contract.
            The server ignores these fields on the pharmacy route too, so this is the honest face of
            a rule that is actually enforced rather than a disabled input in front of an open API. */}
        <Section title="Limits">
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
            Set by OwlRx for this partner. Contact your account executive to change them.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            <ReadOnlyLimit label="Rate / min" value={limits.rate} dflt="60" />
            <ReadOnlyLimit label="Daily quota" value={limits.quota} dflt="10,000" />
            <ReadOnlyLimit label="Max rows / page" value={limits.page} dflt="1,000" />
            <ReadOnlyLimit label="Query timeout" value={limits.timeout} dflt="30" suffix="s" />
          </div>
        </Section>
        </>)}

        {tab === 'webhooks' && (
          /* D-HOOK-1: push a copy of each matching PioneerRx envelope to this partner's endpoint(s) */
          <DataApiWebhooksSection client={client} providers={providers} onError={onError} />
        )}

        {tab === 'data' && (<>
        <Section title={`Providers (${scope.size} selected)`}>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 6 }}>This client sees only prescriptions from the providers you select.</div>
          <MultiSelectDropdown
            items={providers.map(p => ({ id: p.id, label: p.name }))}
            selected={scope} onChange={setScope} placeholder="Search providers…" emptyLabel="No providers found for this pharmacy." />
          <div style={{ marginTop: 8 }}><button style={btn} disabled={busy} onClick={saveScope}>Save providers</button></div>
        </Section>
        <Section title={`Fields (${grantedCount} of ${fields.length} granted)`}>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
            <strong>Raw</strong> returns the real value, <strong>Masked</strong> returns <code>***</code>, <strong>Hidden</strong> omits the field. Search to find a field fast.
          </div>
          <FieldGrantEditor fields={fields} modes={modes} onChange={setModes} />
          <div style={{ marginTop: 10 }}><button style={btnPrimary} disabled={busy} onClick={saveGrants}>Save fields</button></div>
        </Section>
        <Section title={`Reports (${reportScope.size} exposed)`}>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 6 }}>
            Expose a published report to this client — its own SQL defines exactly what data (shipping, CareRx, plan of treatment, anything) it returns. The client pulls it at <code>/api/data/v1/reports/&#123;id&#125;</code>.
          </div>
          {reports.length === 0
            ? <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>No published reports for this pharmacy yet. Create one in the Report Builder, then expose it here.</div>
            : <>
                <MultiSelectDropdown items={reports.map(r => ({ id: r.id, label: r.name }))} selected={reportScope} onChange={setReportScope} placeholder="Search reports…" emptyLabel="No reports." />
                <div style={{ marginTop: 8 }}><button style={btn} disabled={busy} onClick={saveReports}>Save reports</button></div>
              </>}
        </Section>
        </>)}

        {tab === 'audit' && <ClientAuditSection clientId={client.id} onError={onError} />}
      </div>
    </div>
  )
}

// Searchable multi-select dropdown — scales to hundreds of items.
export function MultiSelectDropdown({ items, selected, onChange, placeholder, emptyLabel }: {
  items: { id: string; label: string }[]; selected: Set<string>; onChange: (s: Set<string>) => void; placeholder: string; emptyLabel: string
}) {
  const [open, setOpen] = useState(false); const [q, setQ] = useState('')
  const shown = useMemo(() => { const t = q.trim().toLowerCase(); return t ? items.filter(i => i.label.toLowerCase().includes(t)) : items }, [items, q])
  const toggle = (id: string) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); onChange(n) }
  const selNames = items.filter(i => selected.has(i.id)).map(i => i.label)
  return (
    <div style={{ position: 'relative', maxWidth: 460 }}>
      <button type="button" style={{ ...inp, width: '100%', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', textAlign: 'left' }} onClick={() => setOpen(o => !o)}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected.size ? 'inherit' : 'var(--color-text-soft)' }}>
          {selected.size === 0 ? 'None selected' : selected.size <= 3 ? selNames.join(', ') : `${selected.size} selected`}
        </span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 41, marginTop: 4, background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #ddd)', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 8, borderBottom: '1px solid var(--color-border, #eee)' }}>
              <Search size={13} color="var(--color-text-soft)" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} style={{ ...inp, border: 'none', flex: 1, padding: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '6px 10px', fontSize: 12, borderBottom: '1px solid var(--color-border, #eee)' }}>
              <button style={linkBtn} onClick={() => onChange(new Set([...selected, ...shown.map(i => i.id)]))}>Select all shown</button>
              <button style={linkBtn} onClick={() => { const n = new Set(selected); shown.forEach(i => n.delete(i.id)); onChange(n) }}>Clear shown</button>
              <span style={{ marginLeft: 'auto', color: 'var(--color-text-soft)' }}>{selected.size} selected</span>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', padding: 6 }}>
              {shown.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-soft)', padding: 8 }}>{q ? 'No matches.' : emptyLabel}</div>}
              {shown.map(i => (
                <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 6px', cursor: 'pointer', borderRadius: 4 }}>
                  <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} /> {i.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Searchable field list with a compact 3-way mode toggle per field — scales to 400+ fields.
function FieldGrantEditor({ fields, modes, onChange }: { fields: api.DataApiField[]; modes: Record<string, Mode>; onChange: (m: Record<string, Mode>) => void }) {
  const [q, setQ] = useState('')
  const shown = useMemo(() => { const t = q.trim().toLowerCase(); return t ? fields.filter(f => f.field.toLowerCase().includes(t) || f.classification.toLowerCase().includes(t)) : fields }, [fields, q])
  const setShownTo = (mode: Mode) => { const m = { ...modes }; shown.forEach(f => { m[f.field] = mode }); onChange(m) }
  return (
    <div style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, borderBottom: '1px solid var(--color-border, #eee)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 180 }}>
          <Search size={13} color="var(--color-text-soft)" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search fields…" style={{ ...inp, border: 'none', flex: 1, padding: 0 }} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Set shown:</span>
        {MODES.map(m => <button key={m} style={{ ...linkBtn, color: MODE_META[m].color }} onClick={() => setShownTo(m)}>{MODE_META[m].label}</button>)}
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {shown.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-soft)', padding: 12 }}>No matching fields.</div>}
        {shown.map(f => {
          const cur = modes[f.field] ?? 'hidden'
          return (
            <div key={f.field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderBottom: '1px solid var(--color-border, #f2f2f2)' }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                <span>{f.field}</span>
                {f.is_phi && <span style={{ fontSize: 10, color: '#dc2626', marginLeft: 6, fontWeight: 600 }}>PHI</span>}
                {f.classification === 'financial' && <span style={{ fontSize: 10, color: '#d97706', marginLeft: 6 }} title="Your cost/margin, not the patient's data">YOUR COST/MARGIN — NOT PHI</span>}
              </div>
              <ModeToggle value={cur} onChange={m => onChange({ ...modes, [f.field]: m })} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ModeToggle({ value, onChange }: { value: Mode; onChange: (m: Mode) => void }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--color-border, #ddd)', borderRadius: 5, overflow: 'hidden', flexShrink: 0 }}>
      {MODES.map(m => {
        const active = value === m
        return (
          <button key={m} onClick={() => onChange(m)} title={MODE_META[m].label}
            style={{ fontSize: 11, padding: '3px 9px', border: 'none', cursor: 'pointer', background: active ? MODE_META[m].color : 'transparent', color: active ? '#fff' : 'var(--color-text-soft)', fontWeight: active ? 600 : 400 }}>
            {MODE_META[m].label}
          </button>
        )
      })}
    </div>
  )
}

// Per-client audit — the disclosure log (who pulled what, when). Loads on demand.
function ClientAuditSection({ clientId, onError }: { clientId: string; onError: (e: string) => void }) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<api.DataApiDisclosure[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 50
  const fx = useServerFunnels(CLIENT_AUDIT_FUNNEL_COLS)
  // A range funnel refetches as it is typed; only the newest request may land.
  const reqSeq = useRef(0)

  // Server-paged: this is the §164.528 record with six-year retention, so it is the largest table
  // on the surface. It used to fetch TOP 200 with no way to reach the 201st row — a capped
  // accounting record, which is the wrong kind of cap.
  const fetchPage = (pg: number) => {
    const seq = ++reqSeq.current
    setLoading(true)
    api.getDataApiClientAudit(clientId, { page: pg, page_size: PAGE_SIZE, filter: fx.filter })
      .then(r => { if (seq !== reqSeq.current) return; setRows(r.disclosures); setTotal(r.total); setPage(r.page) })
      .catch(e => { if (seq === reqSeq.current) onError(friendlyError(e)) })
      .finally(() => { if (seq === reqSeq.current) setLoading(false) })
  }
  function open_() {
    setOpen(o => !o)
    if (rows === null) fetchPage(0)
  }
  // A changed funnel refetches from the first page. Nothing is fetched before the first open.
  useEffect(() => { if (rows !== null) fetchPage(0) }, [fx.sig])  // eslint-disable-line react-hooks/exhaustive-deps
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const fieldsShort = (json: string | null) => { if (!json) return ''; try { const a = JSON.parse(json) as string[]; return a.length <= 3 ? a.join(', ') : `${a.slice(0, 3).join(', ')} +${a.length - 3}` } catch { return '' } }
  return (
    <Section title="Audit — data disclosed to this client">
      <button style={btn} onClick={open_}><ScrollText size={13} /> {open ? 'Hide' : 'View'} activity</button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>}
          {rows && rows.length === 0 && !fx.active && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No calls recorded yet.</div>}
          {rows && (rows.length > 0 || fx.active) && fx.chips}
          {rows && (rows.length > 0 || fx.active) && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
              <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: 'var(--color-text-mute, #6b7280)', paddingTop: 6 }}>Disclosures recorded for this partner under 45 CFR 164.528.</caption>
                <thead><tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border, #eee)' }}>
                  <th style={th}>When{fx.funnel('requested_at')}</th><th style={th}>Resource{fx.funnel('resource_version')}</th><th style={th}>Rows{fx.funnel('row_count')}</th><th style={th}>Patients{fx.funnel('patient_count')}</th><th style={th}>Fields{fx.funnel('fields')}</th><th style={th}>Status{fx.funnel('status')}</th>
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={6} style={{ ...td, color: 'var(--color-text-soft)' }}>{loading ? 'Loading…' : 'No calls match these filters.'}</td></tr>}
                  {rows.map((d, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--color-border, #f4f4f4)' }}>
                      <td style={td}>{formatUsDate(d.requested_at)}</td>
                      <td style={td}>{d.resource}{d.version ? ` (${d.version})` : ''}</td>
                      <td style={td}>{d.row_count}</td>
                      <td style={td}>{d.patient_count}</td>
                      <td style={td} title={d.fields ?? ''}>{fieldsShort(d.fields)}</td>
                      <td style={td}>{d.status ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows && total > PAGE_SIZE && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <button type="button" style={btn} disabled={page === 0 || loading} onClick={() => fetchPage(page - 1)}>Previous</button>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }} aria-live="polite">
                Page {page + 1} of {pageCount} · {total} disclosure{total === 1 ? '' : 's'}
              </span>
              <button type="button" style={btn} disabled={page >= pageCount - 1 || loading} onClick={() => fetchPage(page + 1)}>Next</button>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-soft)', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  )
}

const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--color-text-soft)' }
const inp: React.CSSProperties = { fontSize: 13, padding: '6px 8px', border: '1px solid var(--color-border, #ddd)', borderRadius: 5, background: 'var(--color-bg, #fff)' }
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--color-border, #f0f0f0)' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 10px', border: '1px solid var(--color-border, #ddd)', borderRadius: 5, background: 'var(--color-bg, #fff)', cursor: 'pointer' }
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--color-accent, #2563eb)', color: '#fff', border: '1px solid var(--color-accent, #2563eb)' }
// Destructive, irreversible actions (revoke a key). Deliberately NOT the same weight as the
// reversible action beside it — a permanent cut-off should not look like a pause.
const btnDanger: React.CSSProperties = { ...btn, color: 'var(--color-danger, #b91c1c)', borderColor: 'var(--color-danger, #b91c1c)' }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--color-accent, #2563eb)' }
const th: React.CSSProperties = { padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '5px 10px', whiteSpace: 'nowrap' }
